package com.evoapps.evoshift.photo

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Canvas
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.FilterQuality
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.Paint
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import com.evoapps.evoshift.domain.usecase.user.ProfilePhoto
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlin.math.max
import kotlin.math.roundToInt

/** A picked photo, decoded, upright and at most [ProfilePhotoProcessor.MAX_WORKING_SIDE] pixels. */
class PreparedPhoto(val image: ImageBitmap) {
    val width: Int get() = image.width
    val height: Int get() = image.height
}

/** The cropped photo: the JPEG to upload and the same picture to show while it uploads. */
class RenderedPhoto(val jpeg: ByteArray, val preview: ImageBitmap)

/** A picked file the app cannot use as a photo. */
class PhotoProblemException(val problem: Problem) : Exception("Photo refused: $problem") {
    enum class Problem {
        /** Not an image (or a format the app does not read). */
        NOT_AN_IMAGE,
        /** Over [ProfilePhoto.MAX_INPUT_BYTES] or [ProfilePhoto.MAX_INPUT_PIXELS]. */
        TOO_LARGE,
        /** An image header, but the platform could not decode it (damaged, or e.g. HEIC on the web). */
        UNREADABLE
    }
}

/**
 * Turns a picked file into the uploaded profile photo, on the device (SH-94): checks it, applies
 * the EXIF orientation, lets the person crop, scales the square to 512×512 and encodes it as a
 * JPEG of about 85 % quality. The JPEG is encoded from pixels, so nothing of the original's
 * metadata (EXIF location, camera, dates) leaves the device.
 */
interface ProfilePhotoProcessor {
    /** Decodes [bytes]; fails with [PhotoProblemException]. */
    suspend fun prepare(bytes: ByteArray): PreparedPhoto

    /** The square [crop] (image pixels of [photo]) as the photo to upload. */
    suspend fun render(photo: PreparedPhoto, crop: IntRect): RenderedPhoto

    companion object {
        /** Picked photos are scaled down to this before cropping (plenty for a 512 px result). */
        const val MAX_WORKING_SIDE = 2048
    }
}

/** The platform decoder: null when [bytes] cannot be decoded. May return a larger image than [maxSide]. */
internal expect fun decodeImage(bytes: ByteArray, maxSide: Int): ImageBitmap?

/** Encodes [image] as a baseline JPEG of [quality] (0..100), without any metadata. */
internal expect suspend fun encodeJpeg(image: ImageBitmap, quality: Int): ByteArray

/** [ProfilePhotoProcessor] with the platform's decoder and encoder and Compose's canvas. */
class PlatformProfilePhotoProcessor : ProfilePhotoProcessor {

    override suspend fun prepare(bytes: ByteArray): PreparedPhoto = withContext(Dispatchers.Default) {
        if (bytes.size > ProfilePhoto.MAX_INPUT_BYTES) throw PhotoProblemException(PhotoProblemException.Problem.TOO_LARGE)
        val info = ImageBytes.sniff(bytes) ?: throw PhotoProblemException(PhotoProblemException.Problem.NOT_AN_IMAGE)
        if ((info.pixels ?: 0) > ProfilePhoto.MAX_INPUT_PIXELS) throw PhotoProblemException(PhotoProblemException.Problem.TOO_LARGE)
        val orientation = if (info.format == ImageFormat.JPEG) ImageBytes.jpegOrientation(bytes) else 1
        // Without its EXIF no decoder can rotate the picture on its own: the orientation is applied once, here.
        val clean = if (info.format == ImageFormat.JPEG) ImageBytes.stripJpegMetadata(bytes) else bytes
        val decoded = runCatching { decodeImage(clean, ProfilePhotoProcessor.MAX_WORKING_SIDE) }.getOrNull()
            ?: throw PhotoProblemException(PhotoProblemException.Problem.UNREADABLE)
        PreparedPhoto(upright(decoded, orientation, ProfilePhotoProcessor.MAX_WORKING_SIDE))
    }

    override suspend fun render(photo: PreparedPhoto, crop: IntRect): RenderedPhoto {
        val preview = withContext(Dispatchers.Default) { square(photo.image, crop, ProfilePhoto.SIDE) }
        var jpeg = encodeJpeg(preview, ProfilePhoto.JPEG_QUALITY)
        // A 512 px JPEG is ~50-150 kB; only noise-like pictures come near the 1 MB of storage.rules.
        if (jpeg.size >= ProfilePhoto.MAX_UPLOAD_BYTES) jpeg = encodeJpeg(preview, 60)
        return RenderedPhoto(jpeg, preview)
    }

    companion object {
        private fun paint() = Paint().apply { filterQuality = FilterQuality.High; isAntiAlias = true }

        /** [image] turned upright for EXIF [orientation] and scaled to at most [maxSide]. */
        fun upright(image: ImageBitmap, orientation: Int, maxSide: Int): ImageBitmap {
            val (width, height) = ExifOrientation.size(orientation, image.width, image.height)
            val scale = minOf(1f, maxSide.toFloat() / max(width, height))
            if (orientation == 1 && scale == 1f) return image
            val target = ImageBitmap(max(1, (width * scale).roundToInt()), max(1, (height * scale).roundToInt()))
            Canvas(target).apply {
                scale(scale, scale)
                ExifOrientation.operations(orientation, image.width, image.height).forEach { op ->
                    when (op) {
                        is ExifOrientation.Op.Translate -> translate(op.x, op.y)
                        is ExifOrientation.Op.Rotate -> rotate(op.degrees)
                        is ExifOrientation.Op.Scale -> scale(op.x, op.y)
                    }
                }
                drawImage(image, Offset.Zero, paint())
            }
            return target
        }

        /**
         * The square [crop] of [image] scaled to [side]×[side] on white (a transparent PNG has no
         * black corners as a JPEG). Large reductions go in halving steps, which keeps the result
         * sharp without aliasing.
         */
        fun square(image: ImageBitmap, crop: IntRect, side: Int): ImageBitmap {
            val bounded = IntRect(
                crop.left.coerceIn(0, image.width - 1), crop.top.coerceIn(0, image.height - 1),
                crop.right.coerceIn(1, image.width), crop.bottom.coerceIn(1, image.height)
            )
            var source = image
            var rect = bounded
            while (rect.width > side * 2 && rect.height > side * 2) {
                val step = ImageBitmap(rect.width / 2, rect.height / 2)
                Canvas(step).drawImageRect(source, IntOffset(rect.left, rect.top), IntSize(rect.width, rect.height), IntOffset.Zero, IntSize(step.width, step.height), paint())
                source = step
                rect = IntRect(0, 0, step.width, step.height)
            }
            val result = ImageBitmap(side, side)
            Canvas(result).apply {
                drawRect(Rect(Offset.Zero, Size(side.toFloat(), side.toFloat())), Paint().apply { color = Color.White })
                drawImageRect(source, IntOffset(rect.left, rect.top), IntSize(rect.width, rect.height), IntOffset.Zero, IntSize(side, side), paint())
            }
            return result
        }
    }
}

/**
 * EXIF orientations (1..8) as canvas operations that draw the stored pixels upright, in the
 * order they are applied to the canvas (each maps a point of the stored image).
 */
object ExifOrientation {
    sealed interface Op {
        data class Translate(val x: Float, val y: Float) : Op
        data class Rotate(val degrees: Float) : Op
        data class Scale(val x: Float, val y: Float) : Op
    }

    /** The upright size of a stored [width]×[height] image: 5-8 are quarter turns. */
    fun size(orientation: Int, width: Int, height: Int): Pair<Int, Int> =
        if (orientation in 5..8) height to width else width to height

    fun operations(orientation: Int, width: Int, height: Int): List<Op> {
        val w = width.toFloat()
        val h = height.toFloat()
        return when (orientation) {
            2 -> listOf(Op.Translate(w, 0f), Op.Scale(-1f, 1f))
            3 -> listOf(Op.Translate(w, h), Op.Rotate(180f))
            4 -> listOf(Op.Translate(0f, h), Op.Scale(1f, -1f))
            5 -> listOf(Op.Scale(-1f, 1f), Op.Rotate(90f)) // transpose
            6 -> listOf(Op.Translate(h, 0f), Op.Rotate(90f))
            7 -> listOf(Op.Translate(h, w), Op.Scale(1f, -1f), Op.Rotate(90f)) // transverse
            8 -> listOf(Op.Translate(0f, w), Op.Rotate(-90f))
            else -> emptyList()
        }
    }

    /** Where [operations] put the stored point ([x], [y]): what the canvas does, for tests. */
    fun map(ops: List<Op>, x: Float, y: Float): Pair<Float, Float> =
        ops.foldRight(x to y) { op, (px, py) ->
            when (op) {
                is Op.Translate -> (px + op.x) to (py + op.y)
                is Op.Scale -> (px * op.x) to (py * op.y)
                is Op.Rotate -> when (((op.degrees % 360f) + 360f) % 360f) {
                    90f -> -py to px
                    180f -> -px to -py
                    270f -> py to -px
                    else -> px to py
                }
            }
        }
}
